import type { FragmentType } from '@/generated/gql/fragment-masking'
import { useFragment } from '@/generated/gql/fragment-masking'
import { graphql } from '@/generated/gql/gql'
import { capitalise } from '@/helpers/strings'
import {
  Box,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Card,
  CardHeader,
  CardContent,
  Grid,
} from '@mui/material'

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import CreditsAndSubsidyCard from './creditsAndSubsidy'
import { DateSelectionCard } from './dateSelection'
import LocationSelectionCard from './locationSelection'
import type { MenuProviderVendor } from './menuProviders'
import MenuProviders from './menuProviders'
import { OrderCutOff } from './OrderCutOff'
import ServiceSelectionCard from './serviceSelection'
import { TimeslotSelectionCard } from './timeslotSelection'

const canteenMenuHeaderUserDataFragment = graphql(`
  fragment CanteenMenuHeader_UserDataFragmentV2 on User {
    accountLocation {
      timezone
    }
    ...CanteenLocationSelection_UserDataFragmentV2
    ...CanteenDateSelection_UserDataFragmentV2
    firstName
    wallet {
      balance
    }
    subsidySettings {
      unlimitedSubsidy
    }
    account {
      userInterfaceSettings {
        hideCCPrices
      }
    }
  }
`)

const canteenMenuHeaderCurrentMenuDataFragment = graphql(`
  fragment CanteenMenuHeader_MenuDataFragmentV2 on CCMealService {
    ...CanteenTimeslotSelection_MenuDataFragmentV2
    availableSubsidy
    timeslots {
      id
      vendorMenus {
        id
        vendor {
          id
          logo
          companyName
        }
      }
    }
  }
`)

interface MenuHeaderV2Props {
  userData: FragmentType<typeof canteenMenuHeaderUserDataFragment>
  menuData?: FragmentType<typeof canteenMenuHeaderCurrentMenuDataFragment> | null
  arrivalTime?: string | null
  isClosed: boolean
  meal: string
  date: string
  orderTimeRemaining: number
  onChangeSelectedService: (input: { meal: string; date: string }) => void
  onChangeAccountLocation: (accountLocationId: number) => void
  onChangeTimeslot: (timeslot: string) => void
  shouldShowMenusByList?: boolean
}

export function MenuHeaderV2({
  userData: userDataProp,
  menuData: menuDataProp,
  arrivalTime,
  isClosed,
  meal,
  date,
  orderTimeRemaining,
  onChangeSelectedService,
  onChangeAccountLocation,
  onChangeTimeslot,
  shouldShowMenusByList = true,
}: MenuHeaderV2Props) {
  const userData = useFragment(canteenMenuHeaderUserDataFragment, userDataProp)
  const menuData = useFragment(canteenMenuHeaderCurrentMenuDataFragment, menuDataProp)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { t } = useTranslation()

  const {
    firstName,
    wallet: { balance } = { balance: 0 },
    subsidySettings: { unlimitedSubsidy },
    account,
  } = userData

  const { userInterfaceSettings: { hideCCPrices = false } = {} } = account || {}
  const { availableSubsidy: subsidy = 0 } = menuData || {}

  const vendorData = useMemo<MenuProviderVendor[]>(() => {
    const vendorMenus = menuData?.timeslots?.flatMap((timeslot) => timeslot.vendorMenus ?? []) ?? []
    const uniqueVendorsById = new Map(vendorMenus.map(({ vendor }) => [vendor.id, vendor]))
    return Array.from(uniqueVendorsById.values())
  }, [menuData])

  const handleTimeslotSelection = (value: string) => {
    onChangeTimeslot(value)
  }

  const title = useMemo(() => {
    return (
      <Typography variant="h4" component="div">
        Hi{' '}
        <Typography variant="inherit" component="span" sx={{ color: 'primary.main' }}>
          {capitalise(firstName)}
        </Typography>
        ,{' '}
        {isClosed
          ? t('cc:navBar.serviceClosed')
          : t('cc:whatDoYouFancyForMeal', { context: meal, defaultValue: meal })}
      </Typography>
    )
  }, [firstName, isClosed, meal, t])

  return (
    <Box>
      <Card>
        {!isMobile ? (
          <CardHeader
            title={title}
            action={shouldShowMenusByList && <MenuProviders vendorData={vendorData} />}
            sx={{
              padding: '8px 16px 0px',
              '& .MuiCardHeader-action': { margin: 0, alignSelf: 'center' },
            }}
          />
        ) : null}
        <CardContent sx={{ '&:last-child': { paddingBottom: 2 } }}>
          <Grid container spacing={2}>
            <Grid size={hideCCPrices ? 12 : { xs: 12, sm: 12, md: 8, lg: 9 }}>
              <Stack
                sx={{
                  height: '100%',
                  border: '1px solid',
                  borderRadius: 2,
                  borderColor: 'divider',
                  '& .MuiOutlinedInput-notchedOutline, .MuiInput-underline': {
                    border: 'none',
                  },
                  '& .MuiFormLabel-root, .MuiInputLabel-root': {
                    backgroundColor: theme.palette.background.paper,
                  },
                  '& .MuiSelect-select, .MuiTextField, .MuiInputBase-input': {
                    fontWeight: '600',
                    fontSize: '14px',
                  },
                }}>
                <Grid
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    padding: 0,
                  }}>
                  <Stack
                    sx={{ padding: '8px 16px' }}
                    direction="row"
                    justifyContent={'space-between'}
                    alignItems={'center'}>
                    <Stack direction="row" alignItems="center" flexGrow={1}>
                      {!isMobile && (
                        <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap', mr: 0.5 }}>
                          {t('cc:navBar.orderingFor')}
                        </Typography>
                      )}
                      <Stack flexGrow={3}>
                        <DateSelectionCard
                          userData={userData}
                          meal={meal}
                          date={date}
                          onChangeSelectedService={onChangeSelectedService}
                        />
                      </Stack>
                    </Stack>
                    <Stack flexGrow={3}>
                      {isMobile && (
                        <OrderCutOff showLabel={false} orderTimeRemaining={orderTimeRemaining} />
                      )}
                    </Stack>
                  </Stack>
                </Grid>
                <Grid container padding={0} sx={{ flexGrow: 1 }}>
                  <Grid size={!isMobile ? 2 : 4}>
                    <ServiceSelectionCard meal={meal} />
                  </Grid>

                  <Grid size={4} sx={{ borderLeft: '1px solid', borderColor: 'divider' }}>
                    <LocationSelectionCard
                      userData={userData}
                      onChangeAccountLocation={onChangeAccountLocation}
                    />
                  </Grid>

                  <Grid
                    size={!isMobile ? 3 : 4}
                    sx={{ borderLeft: '1px solid', borderColor: 'divider' }}>
                    <TimeslotSelectionCard
                      timezone={userData.accountLocation?.timezone}
                      timeslotData={menuData}
                      arrivalTime={arrivalTime}
                      date={date}
                      onChangeTimeslot={handleTimeslotSelection}
                    />
                  </Grid>

                  {!isMobile && (
                    <Grid size={3} sx={{ borderLeft: '1px solid', borderColor: 'divider' }}>
                      <OrderCutOff showLabel={true} orderTimeRemaining={orderTimeRemaining} />
                    </Grid>
                  )}
                </Grid>

                {!hideCCPrices && isMobile && (
                  <CreditsAndSubsidyCard
                    variant="compact"
                    subsidy={subsidy}
                    unlimitedSubsidy={unlimitedSubsidy}
                    balance={balance}
                  />
                )}
              </Stack>
            </Grid>

            {!hideCCPrices && !isMobile && (
              <CreditsAndSubsidyCard
                subsidy={subsidy}
                unlimitedSubsidy={unlimitedSubsidy}
                balance={balance}
              />
            )}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  )
}
